import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useRef } from 'react';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import AddIcon from '../../assets/icons/plus.svg';
import { useTranslation } from 'react-i18next';
import { deleteAdminTaskAnswer, updateAdminTask } from '../../api/admin/tasks';
import { setIsModalOpen, setModalContent, setModalContentProps } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import Animate from '../../ui/Animate';
import { selectTaskAnswerToDeleteId, setTaskAnswerToDeleteId, } from '../../store/adminSlices/rallies';
import AdminTaskAnswerCard from '../../ui/AdminTaskAnswerCard/SelectionTaskAnswerCard';
import { generateId } from '../../helpers';
const AdminTaskAnswersList = ({ taskId, formState, formErrors, validateInputField, answersError, setTaskAnswersError, setUpdatedFormFields, setSortedAnswers, setTaskUpdated, }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const taskAnswerToDeleteId = useSelector(selectTaskAnswerToDeleteId);
    const answersRefs = useRef({});
    const animateRef = useRef(null);
    const hasAnswersEmptyFields = (answers) => {
        return answers.some((answer) => {
            const translationEntries = Object.entries(answer.translations);
            for (const [text] of translationEntries) {
                if (!text?.trim()) {
                    return true;
                }
            }
            return false;
        });
    };
    const showValidationErrorModal = ({ titleTranslationKey, messageTranslationKey, }) => {
        dispatch(setIsModalOpen(true));
        dispatch(setModalContent(MODAL_CONTENT.cannotDeleteTaskAnswer.key));
        dispatch(setModalContentProps({
            title: t(titleTranslationKey),
            ...(messageTranslationKey ? { message: t(messageTranslationKey) } : {}),
        }));
        dispatch(setTaskAnswerToDeleteId(null));
    };
    const deleteBackendTaskAnswer = async (uid) => {
        try {
            await updateAdminTask(taskId, {
                answers: formState.taskAnswers.map((answer) => ({
                    ...(answer.uid ? { uid: answer.uid } : {}),
                    content: answer.translations,
                    isCorrect: answer.isCorrect,
                    sort: answer.sort,
                })),
            });
            await deleteAdminTaskAnswer(String(uid));
            dispatch(setTaskAnswerToDeleteId(null));
            setTaskUpdated(true);
        }
        catch (error) {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    useEffect(() => {
        const handleDeleteTaskAnswer = async (answerToDelete) => {
            const answerToDeleteBackendId = answerToDelete?.uid;
            if (answerToDeleteBackendId) {
                if (formState.taskStatus === 1) {
                    const otherAnswers = formState.taskAnswers.filter((answer) => answer.clientId !== answerToDelete.clientId);
                    const correctAnswersAmount = otherAnswers.reduce((acc, current) => acc + (current.isCorrect ? 1 : 0), 0);
                    if (answerToDelete.isCorrect) {
                        showValidationErrorModal({
                            titleTranslationKey: 'admin.cannot_delete_task_correct_answer',
                        });
                    }
                    else if (otherAnswers.length < 2) {
                        showValidationErrorModal({
                            titleTranslationKey: 'admin.cannot_delete_task_answer_title',
                            messageTranslationKey: 'admin.min_two_answers_required',
                        });
                    }
                    else if (hasAnswersEmptyFields(otherAnswers)) {
                        showValidationErrorModal({
                            titleTranslationKey: 'admin.cannot_delete_task_answer_title',
                            messageTranslationKey: 'admin.no_answers_empty_fields',
                        });
                    }
                    else if (formState.selectedTaskType === 'single-selection') {
                        if (correctAnswersAmount !== 1) {
                            showValidationErrorModal({
                                titleTranslationKey: 'admin.cannot_delete_task_answer_title',
                                messageTranslationKey: 'admin.cannot_delete_single_task_answer',
                            });
                        }
                        else {
                            await deleteBackendTaskAnswer(answerToDeleteBackendId);
                        }
                    }
                    else if (formState.selectedTaskType === 'multiple-selection') {
                        if (correctAnswersAmount < 2) {
                            showValidationErrorModal({
                                titleTranslationKey: 'admin.cannot_delete_task_answer_title',
                                messageTranslationKey: 'admin.cannot_delete_multiple_task_answer',
                            });
                        }
                        else {
                            await deleteBackendTaskAnswer(answerToDeleteBackendId);
                        }
                    }
                }
                else {
                    await deleteBackendTaskAnswer(answerToDeleteBackendId);
                }
            }
            else {
                formState.setTaskAnswers((prev) => prev.filter((answer) => answer.clientId !== answerToDelete.clientId));
                setUpdatedFormFields((prev) => {
                    const newSet = new Set(prev);
                    newSet.add('taskAnswers');
                    return newSet;
                });
            }
        };
        if (taskAnswerToDeleteId) {
            const answerToDelete = formState.taskAnswers.find((answer) => answer.clientId === taskAnswerToDeleteId);
            if (answerToDelete) {
                handleDeleteTaskAnswer(answerToDelete);
            }
        }
    }, [taskAnswerToDeleteId]);
    const moveAnswer = (index, direction) => {
        const newAnswers = [...formState.taskAnswers];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= formState.taskAnswers.length) {
            return;
        }
        // snapshot positions BEFORE DOM changes
        animateRef.current?.capture();
        [newAnswers[index], newAnswers[targetIndex]] = [newAnswers[targetIndex], newAnswers[index]];
        newAnswers.forEach((answer, index) => (answer.sort = index + 1));
        formState.setTaskAnswers(newAnswers);
        const newTasks = newAnswers.find((answer) => !answer.uid);
        if (!newTasks) {
            setSortedAnswers(newAnswers.map((answer) => answer.uid));
        }
    };
    const onAddAnswer = () => {
        formState.setTaskAnswers((prev) => [
            ...prev,
            {
                uid: null,
                translations: { de: '', en: '', cs: '' },
                isCorrect: false,
                sort: prev.length + 1,
                clientId: generateId('answer'),
            },
        ]);
        setUpdatedFormFields((prev) => {
            const newSet = new Set(prev);
            newSet.add('taskAnswers');
            return newSet;
        });
    };
    const onDeleteAnswer = (answerClientId) => {
        dispatch(setIsModalOpen(true));
        dispatch(setModalContent(MODAL_CONTENT.confirmDeleteTaskAnswer.key));
        dispatch(setModalContentProps({
            answerId: answerClientId,
        }));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "admin-task-answers-list", children: [formState.taskAnswers && (_jsx(Animate, { refs: answersRefs, ref: animateRef, children: formState.taskAnswers.map((answer, index) => (_jsx(AdminTaskAnswerCard, { ref: (el) => {
                                answersRefs.current[answer.clientId] = el;
                            }, moveAnswer: (direction) => moveAnswer(index, direction), taskAnswer: answer, taskIndex: index, formState: formState, disableDown: index === formState.taskAnswers.length - 1, disableUp: index === 0, formErrors: formErrors, validateInputField: validateInputField, setAnswersError: setTaskAnswersError, setUpdatedFormFields: setUpdatedFormFields, onDeleteAnswer: onDeleteAnswer }, answer.clientId))) })), _jsx("div", { className: "task-answer-item selection-task", children: _jsxs("div", { className: "task-answer-item-header", children: [_jsx("div", { className: "task-answer-order-controls", children: _jsx("div", { className: "task-answer-order", children: _jsx("span", { children: formState.taskAnswers.length + 1 }) }) }), _jsx(Input, { color: 'gray', id: 'additional-answer', value: '', name: 'additional-answer', placeholder: t('label.additional_answer'), onChange: () => { }, disabled: true, additionalContent: _jsx(Button, { shape: 'square', icon: _jsx(AddIcon, {}), iconSize: 'small', onClick: onAddAnswer }) })] }) })] }), answersError && (_jsx("div", { className: "admin-task-answers-error form-field-error-message", children: t(answersError) }))] }));
};
export default AdminTaskAnswersList;
